import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { auth, db, storage } from "../firebaseConfig";
import {
  getCompletionOccurrenceKey,
  isRecurring,
  toDateKey,
} from "../utils/recurringOccurrenceUtils";
import { checkOutVisit } from "../clock/jobVisits";
import "../pages/InvoicePPD.css";

const PPD_LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/priority-backend.firebasestorage.app/o/logo-images%2Fprioritypestlogogreyblue.png?alt=media&token=da273ab0-3054-4675-bb96-3a3a4a242434";

const moneyNumber = (value) => {
  const number = Number(String(value ?? "").replace(/[$,]/g, ""));
  return Number.isFinite(number) ? number : 0;
};

const optionText = (option = {}) =>
  option.title ?? option.label ?? option.value ?? "";

// "firstName lastName" -> "firstName L."
const formatUserName = (user = {}) => {
  const first = String(user.firstName || "").trim();
  const lastInitial = String(user.lastName || "").trim().charAt(0);
  if (!first) return "";
  return lastInitial ? `${first} ${lastInitial}.` : first;
};

const chemicalText = (products = []) =>
  products
    .map((item) =>
      item.qty ? `${item.chemical} - ${item.qty}` : item.chemical
    )
    .filter(Boolean)
    .join("\n");

const SignaturePadField = ({ label, value, onChange, disabled, unavailableText }) => {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    context.clearRect(0, 0, canvas.width, canvas.height);
    if (!value) return;
    const image = new Image();
    image.onload = () => {
      const scale = Math.min(
        canvas.width / image.width,
        canvas.height / image.height
      );
      const width = image.width * scale;
      const height = image.height * scale;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(
        image,
        (canvas.width - width) / 2,
        (canvas.height - height) / 2,
        width,
        height
      );
    };
    image.src = value;
  }, [value]);

  const point = (event) => {
    const canvas = canvasRef.current;
    const rectangle = canvas.getBoundingClientRect();
    const pointer = event.touches?.[0] || event;
    return {
      x: (pointer.clientX - rectangle.left) * (canvas.width / rectangle.width),
      y: (pointer.clientY - rectangle.top) * (canvas.height / rectangle.height),
    };
  };

  const start = (event) => {
    if (disabled) return;
    event.preventDefault();
    const context = canvasRef.current.getContext("2d");
    const position = point(event);
    context.strokeStyle = "#111";
    context.lineWidth = 2;
    context.lineCap = "round";
    context.beginPath();
    context.moveTo(position.x, position.y);
    setDrawing(true);
  };

  const draw = (event) => {
    if (!drawing || disabled) return;
    event.preventDefault();
    const context = canvasRef.current.getContext("2d");
    const position = point(event);
    context.lineTo(position.x, position.y);
    context.stroke();
    onChange(canvasRef.current.toDataURL("image/png"));
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontWeight: 800, marginBottom: 6 }}>
        {label}
      </label>
      {unavailableText ? (
        <div
          style={{
            width: "100%",
            maxWidth: 520,
            height: 140,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "2px dashed #94a3b8",
            borderRadius: 8,
            background: "#f1f5f9",
            color: "#64748b",
            fontWeight: 700,
          }}
        >
          {unavailableText}
        </div>
      ) : (
        <>
          <canvas
            ref={canvasRef}
            width={520}
            height={140}
            onMouseDown={start}
            onMouseMove={draw}
            onMouseUp={() => setDrawing(false)}
            onMouseLeave={() => setDrawing(false)}
            onTouchStart={start}
            onTouchMove={draw}
            onTouchEnd={() => setDrawing(false)}
            style={{
              width: "100%",
              maxWidth: 520,
              height: 140,
              display: "block",
              border: "2px solid #94a3b8",
              borderRadius: 8,
              background: "#fff",
              touchAction: "none",
            }}
          />
          <button
            type="button"
            className="red-button"
            disabled={disabled}
            onClick={() => onChange("")}
            style={{ width: "auto", marginTop: 7 }}
          >
            Clear Signature
          </button>
        </>
      )}
    </div>
  );
};

const MultiSelectField = ({
  dropdown,
  selectedValues,
  onToggle,
  disabled,
  background,
}) => {
  const [search, setSearch] = useState("");

  const allOptions = dropdown.options || [];
  const searchLower = search.trim().toLowerCase();
  const filteredOptions = searchLower
    ? allOptions.filter((option) =>
        optionText(option).toLowerCase().includes(searchLower)
      )
    : allOptions;

  // Group by option.category when the data provides one; otherwise render
  // as a single flat list (unchanged behavior when no categories exist).
  const hasCategories = allOptions.some((option) => option?.category);
  const groups = hasCategories
    ? filteredOptions.reduce((acc, option) => {
        const category = option.category || "Other";
        if (!acc[category]) acc[category] = [];
        acc[category].push(option);
        return acc;
      }, {})
    : { "": filteredOptions };

  const renderOption = (option, index) => {
    const text = optionText(option);
    if (!text) return null;
    return (
      <label
        key={`${dropdown.fieldName}_${text}_${index}`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 4,
          padding: "5px 0",
          margin: 0,
          textAlign: "left",
          cursor: disabled ? "default" : "pointer",
        }}
      >
        <input
          type="checkbox"
          disabled={disabled}
          checked={selectedValues.includes(text)}
          onChange={() => onToggle(option)}
          style={{
            width: 16,
            height: 16,
            margin: 0,
            flex: "0 0 16px",
          }}
        />
        <span style={{ margin: 0, lineHeight: 1.25 }}>{text}</span>
      </label>
    );
  };

  return (
    <div
      style={{
        marginBottom: 14,
        padding: 12,
        background,
        border: "1px solid #cbd5e1",
        borderRadius: 10,
      }}
    >
      <div style={{ fontWeight: 800, marginBottom: 7 }}>
        {dropdown.label || dropdown.fieldName}
      </div>

      {allOptions.length > 6 && (
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search…"
          disabled={disabled}
          style={{
            width: "100%",
            boxSizing: "border-box",
            marginBottom: 7,
            padding: 7,
            border: "1px solid #cbd5e1",
            borderRadius: 6,
          }}
        />
      )}

      <div
        style={{
          maxHeight: 190,
          overflowY: "auto",
          background: "#fff",
          border: "1px solid #dbe3ed",
          borderRadius: 8,
          padding: 7,
        }}
      >
        {filteredOptions.length ? (
          Object.entries(groups).map(([category, options]) =>
            options.length ? (
              <div key={category || "flat"} style={{ marginBottom: 6 }}>
                {category && (
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 12,
                      color: "#475569",
                      margin: "6px 0 2px",
                    }}
                  >
                    {category}
                  </div>
                )}
                {options.map((option, index) => renderOption(option, index))}
              </div>
            ) : null
          )
        ) : allOptions.length ? (
          <span style={{ color: "#64748b" }}>No matches for "{search}".</span>
        ) : (
          <span style={{ color: "#64748b" }}>No options configured.</span>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Time In / Time Out come from job check-in (job_visits). See clock/jobVisits.
// ---------------------------------------------------------------------------
const toDateSafe = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === "object" && typeof value.seconds === "number") {
    return new Date(value.seconds * 1000);
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toTimeInput = (date) => {
  if (!date) return "";
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const prettyTime = (date) =>
  date
    ? date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : "";

const normalizePerson = (value = "") =>
  String(value).toLowerCase().replace(/\s+/g, " ").trim();

const assignedTechNames = (job = {}) => {
  const list = Array.isArray(job.techAssign) ? job.techAssign : [job.techAssign];
  return list.map(normalizePerson).filter(Boolean);
};

// All check-ins recorded for THIS occurrence of the job, assigned techs first.
const loadOccurrenceVisits = async (job) => {
  if (!job?.id) return [];
  const snapshot = await getDocs(
    query(collection(db, "job_visits"), where("jobId", "==", job.id))
  );
  const keys = new Set(
    [
      getCompletionOccurrenceKey(job),
      toDateKey(job.occurrenceDate),
      toDateKey(job.__renderDate),
      toDateKey(job.startDate),
    ].filter(Boolean)
  );
  const recurring = isRecurring(job);
  const visits = snapshot.docs
    .map((item) => ({ id: item.id, ...item.data() }))
    // One-time jobs: every visit counts. Recurring: only this occurrence's.
    .filter((visit) => !recurring || keys.has(toDateKey(visit.occurrenceDate)));

  const techs = assignedTechNames(job);
  const byAssigned = visits.filter((visit) =>
    techs.includes(normalizePerson(visit.userName))
  );
  return byAssigned.length ? byAssigned : visits;
};

// Fahrenheit options for the Temperature dropdown.
const TEMPERATURE_OPTIONS = Array.from({ length: 121 }, (_, index) => `${index - 10}°F`);

// Fields that come from the job / property and can't be changed on this form.
const LOCKED_FIELDS = [
  ["invoiceNumber", "Invoice Number"],
  ["siteName", "Site Name"],
  ["accountingEmail", "Accounting Email"],
  ["clientEmail", "Client Email"],
  ["managerEmail", "Manager Email"],
  ["terms", "Terms"],
];

// Local calendar date (not UTC) so evening submissions don't jump a day.
const localDateKey = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const imageUrlOf = (item) =>
  typeof item === "string"
    ? item
    : item?.url || item?.fileURL || item?.downloadURL || "";

export default function JobCompletionInvoiceModal({
  job,
  amount,
  onClose,
  onCompleted,
}) {
  const [invoiceDropdowns, setInvoiceDropdowns] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stage, setStage] = useState("pricing");
  const [form, setForm] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [visitInfo, setVisitInfo] = useState({
    loading: true,
    count: 0,
    stillCheckedIn: false,
  });
  const [timeEdited, setTimeEdited] = useState({ in: false, out: false });
  // Files uploaded during this session, so removing one also deletes it.
  const uploadedPathsRef = useRef(new Set());
  // Check-ins found for this occurrence (closed out on submit).
  const occurrenceVisitsRef = useRef([]);
  const initializedForRef = useRef("");

  const defaultAmount = moneyNumber(
    amount ??
      job?.estimatedTotal ??
      job?.addTotal ??
      job?.total ??
      job?.proposalTotal ??
      job?.amount
  );
  const initialFee = moneyNumber(job?.initialAmount || defaultAmount);
  const monthlyFee = moneyNumber(
    job?.recurringInvoiceAmount ??
      job?.monthlyAmount ??
      job?.monthlyFee ??
      defaultAmount
  );
  const occurrenceKey = job ? getCompletionOccurrenceKey(job) : "";
  const occurrenceDate =
    toDateKey(job?.occurrenceDate) ||
    toDateKey(job?.__renderDate) ||
    toDateKey(job?.startDate) ||
    localDateKey();

  // Build the form ONCE per job occurrence. Parents re-render (and sometimes
  // hand us a fresh job object) while the form is open; re-initializing then
  // would wipe out everything the tech already filled in.
  useEffect(() => {
    if (!job) return;
    const formKey = `${job.id}__${occurrenceKey}`;
    if (initializedForRef.current === formKey) return;
    initializedForRef.current = formKey;

    setForm({
      logo: PPD_LOGO_URL,
      invoiceNumber: String(job.wo || job.id || ""),
      wo: String(job.wo || ""),
      jobId: job.id || "",
      originalJobId: job.originalJobId || job.id || "",
      occurrenceKey,
      companyName: "Priority Pest Defense LLC",
      companyAddress: job.companyAddress || "",
      companyCityStateZip: job.companyCityStateZip || "",
      siteName: job.siteName || job.propertyName || "",
      propertyName: job.propertyName || job.siteName || "",
      management: job.management || "",
      managerName: job.managerName || "",
      managerEmail: job.managerEmail || "",
      accountingEmail: job.accountingEmail || "",
      clientEmail: job.clientEmail || job.email || "",
      address: job.address || "",
      city: job.city || "",
      state: job.state || "",
      zip: job.zip || "",
      cityStateZip:
        job.cityStateZip ||
        [job.city, job.state, job.zip].filter(Boolean).join(", "),
      dateOfService: occurrenceDate,
      serviceDate: occurrenceDate,
      invoiceDate: localDateKey(),
      submittedDate: localDateKey(),
      submittedBy: "",
      timeIn: "",
      timeOut: "",
      checkInAt: null,
      checkOutAt: null,
      timeInSource: "",
      timeOutSource: "",
      purchaseOrder: job.purchaseOrder || job.clientWorkOrder || "None",
      terms: job.terms || "Due Upon Invoice",
      serviceType: job.serviceType || "",
      serviceDescription: job.scope || job.serviceType || "Service",
      quantity: "1.00",
      amount: defaultAmount,
      rate: defaultAmount,
      price: defaultAmount,
      serviceRows: [
        {
          id: `row_${Date.now()}`,
          serviceDescription: job.scope || job.serviceType || "Service",
          quantity: "1.00",
          amount: defaultAmount,
        },
      ],
      initialAmount: initialFee,
      recurringInvoiceAmount: monthlyFee,
      invoicePricingSelection: "",
      dropdownValues: {},
      dropdownSelections: {},
      chemicalProducts: [],
      chemicalUsed: "",
      temperature: "",
      notes: "",
      jobDescription: "",
      technicianSignature: "",
      customerSignature: "",
      customerUnavailableToSign: false,
      images: [],
    });
    setStage("pricing");
    setTimeEdited({ in: false, out: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, occurrenceKey]);

  // Who is filling this out (only Admin may change Time In / Time Out).
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    (async () => {
      let role = null;
      const own = await getDoc(doc(db, "users", user.uid));
      if (own.exists()) {
        role = own.data().role;
      } else {
        const byAuth = await getDocs(
          query(collection(db, "users"), where("authUid", "==", user.uid))
        );
        role = byAuth.docs[0]?.data()?.role || null;
      }
      setIsAdmin(role === "Admin");
    })().catch((error) => console.warn("Could not load role:", error));
  }, []);

  // Time In = first check-in by the assigned tech for this occurrence.
  // Time Out = their check-out, or the completion time if still checked in.
  useEffect(() => {
    if (!job?.id) return;
    let cancelled = false;
    setVisitInfo({ loading: true, count: 0, stillCheckedIn: false });

    loadOccurrenceVisits(job)
      .then((visits) => {
        if (cancelled) return;
        occurrenceVisitsRef.current = visits;
        const checkIns = visits
          .map((visit) => toDateSafe(visit.checkInAt))
          .filter(Boolean)
          .sort((a, b) => a - b);
        const checkOuts = visits
          .map((visit) => toDateSafe(visit.checkOutAt))
          .filter(Boolean)
          .sort((a, b) => b - a);
        const stillCheckedIn = visits.some(
          (visit) => visit.status === "checked_in"
        );

        const firstIn = checkIns[0] || null;
        // Still on site -> they are leaving now, which is the completion time.
        const lastOut = stillCheckedIn ? new Date() : checkOuts[0] || null;

        setVisitInfo({ loading: false, count: visits.length, stillCheckedIn });
        setForm((previous) =>
          previous
            ? {
                ...previous,
                timeIn: firstIn ? toTimeInput(firstIn) : previous.timeIn,
                checkInAt: firstIn ? firstIn.toISOString() : previous.checkInAt,
                timeInSource: firstIn ? "check_in" : previous.timeInSource,
                timeOut: lastOut ? toTimeInput(lastOut) : previous.timeOut,
                checkOutAt: lastOut ? lastOut.toISOString() : previous.checkOutAt,
                timeOutSource: lastOut
                  ? stillCheckedIn
                    ? "completion"
                    : "check_out"
                  : previous.timeOutSource,
              }
            : previous
        );
      })
      .catch((error) => {
        console.warn("Could not load job check-ins:", error);
        if (!cancelled) {
          setVisitInfo({ loading: false, count: 0, stillCheckedIn: false });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id, occurrenceKey]);

  useEffect(() => {
    if (!job) return;
    (async () => {
      const [dropdownSnapshot, propertySnapshot, usersSnapshot] = await Promise.all([
        getDocs(collection(db, "invoiceDropdowns")),
        getDocs(collection(db, "properties")),
        getDocs(collection(db, "users")),
      ]);

      setInvoiceDropdowns(
        dropdownSnapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
      );

      setUsersList(
        usersSnapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((user) => user.firstName || user.lastName)
          .sort((a, b) =>
            String(a.firstName || "").localeCompare(String(b.firstName || ""))
          )
      );

      const propertyList = propertySnapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

      const wanted = String(
        job.siteName || job.propertyName || ""
      ).trim().toLowerCase();

      const property = propertyList.find((item) =>
        [item.propertyName, item.siteName, item.name]
          .filter(Boolean)
          .some((name) => String(name).trim().toLowerCase() === wanted)
      );

      if (property) {
        setForm((previous) =>
          previous
            ? {
                ...previous,
                management: previous.management || property.management || "",
                managerName:
                  previous.managerName || property.managerName || "",
                managerEmail:
                  previous.managerEmail || property.managerEmail || "",
                accountingEmail:
                  previous.accountingEmail || property.accountingEmail || "",
                clientEmail:
                  previous.clientEmail || property.clientEmail || "",
                terms:
                  job.terms || property.terms || previous.terms || "Due Upon Invoice",
                address: previous.address || property.address || "",
                city: previous.city || property.city || "",
                state: previous.state || property.state || "",
                zip: previous.zip || property.zip || "",
                cityStateZip:
                  previous.cityStateZip ||
                  [property.city, property.state, property.zip]
                    .filter(Boolean)
                    .join(", "),
              }
            : previous
        );
      }
    })().catch((error) =>
      console.error("Could not load completion invoice data:", error)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id]);

  // Temperature has its own Fahrenheit dropdown below, so it is left out of
  // the checkbox lists.
  const dynamicDropdowns = useMemo(
    () =>
      invoiceDropdowns.filter((item) =>
        [
          "dropdown",
          "chemicalUsed",
          "pestTreated",
          "areasTreated",
          "windSpeed",
          "equipmentUsed",
          "weather",
          "windDirection",
        ].includes(item.fieldName)
      ),
    [invoiceDropdowns]
  );

  const temperatureLabel = useMemo(() => {
    const configured = invoiceDropdowns.find(
      (item) => item.fieldName === "temperature"
    );
    return configured?.label || "Temperature";
  }, [invoiceDropdowns]);

  const generatedDescription = useMemo(() => {
    if (!form) return "";
    const blocks = [];
    dynamicDropdowns.forEach((dropdown) => {
      const field = dropdown.fieldName;
      if (field === "chemicalUsed") {
        const text = chemicalText(form.chemicalProducts || []);
        if (text) blocks.push(`${dropdown.label || "Chemical Used"}:\n${text}`);
        return;
      }
      const selected = form.dropdownSelections?.[field] || [];
      if (selected.length) {
        // The generic "dropdown" category has no meaningful label of its
        // own — don't prefix its selections with a "Dropdown:" heading.
        if (field === "dropdown" && !dropdown.label) {
          blocks.push(selected.join("\n"));
        } else {
          blocks.push(`${dropdown.label || field}:\n${selected.join("\n")}`);
        }
      }
    });
    if (form.temperature) {
      blocks.push(`${temperatureLabel}:\n${form.temperature}`);
    }
    return blocks.join("\n\n");
  }, [form, dynamicDropdowns, temperatureLabel]);

  if (!job || !form) return null;

  const change = (field, value) =>
    setForm((previous) => ({ ...previous, [field]: value }));

  const changeTime = (which, value) => {
    if (!isAdmin) return;
    setTimeEdited((previous) => ({ ...previous, [which]: true }));
    setForm((previous) =>
      which === "in"
        ? { ...previous, timeIn: value, timeInSource: "admin" }
        : { ...previous, timeOut: value, timeOutSource: "admin" }
    );
  };

  const updateServiceRow = (id, field, value) =>
    setForm((previous) => ({
      ...previous,
      serviceRows: previous.serviceRows.map((row) =>
        row.id === id ? { ...row, [field]: value } : row
      ),
    }));

  const addServiceRow = () =>
    setForm((previous) => ({
      ...previous,
      serviceRows: [
        ...previous.serviceRows,
        {
          id: `row_${Date.now()}_${Math.random().toString(36).slice(2)}`,
          serviceDescription: "",
          quantity: "1.00",
          amount: "",
        },
      ],
    }));

  const removeServiceRow = (id) =>
    setForm((previous) => ({
      ...previous,
      serviceRows:
        previous.serviceRows.length > 1
          ? previous.serviceRows.filter((row) => row.id !== id)
          : previous.serviceRows,
    }));

  const invoiceTotal = (form.serviceRows || []).reduce(
    (sum, row) => sum + moneyNumber(row.quantity) * moneyNumber(row.amount),
    0
  );

  const choosePricing = (selection) => {
    const selectedAmount = selection === "initial" ? initialFee : monthlyFee;
    setForm((previous) => ({
      ...previous,
      invoicePricingSelection: selection,
      amount: selectedAmount,
      rate: selectedAmount,
      price: selectedAmount,
      serviceRows: previous.serviceRows.map((row, index) =>
        index === 0 ? { ...row, amount: selectedAmount } : row
      ),
    }));
    setStage("form");
  };

  const toggleDropdown = (dropdown, option) => {
    const field = dropdown.fieldName;
    const text = optionText(option);

    if (field === "chemicalUsed") {
      const exists = form.chemicalProducts.some(
        (item) => item.chemical === text
      );
      if (exists) {
        const products = form.chemicalProducts.filter(
          (item) => item.chemical !== text
        );
        setForm((previous) => ({
          ...previous,
          chemicalProducts: products,
          chemicalUsed: chemicalText(products),
          dropdownSelections: {
            ...(previous.dropdownSelections || {}),
            chemicalUsed: products.map((item) => item.chemical),
          },
        }));
        return;
      }

      const quantity = window.prompt(
        `Enter product quantity for "${text}"`,
        ""
      );
      if (quantity === null) return;
      const products = [
        ...form.chemicalProducts,
        { chemical: text, qty: quantity.trim() },
      ];
      setForm((previous) => ({
        ...previous,
        chemicalProducts: products,
        chemicalUsed: chemicalText(products),
        dropdownSelections: {
          ...(previous.dropdownSelections || {}),
          chemicalUsed: products.map((item) => item.chemical),
        },
      }));
      return;
    }

    setForm((previous) => {
      const selected = previous.dropdownSelections?.[field] || [];
      const next = selected.includes(text)
        ? selected.filter((item) => item !== text)
        : [...selected, text];
      return {
        ...previous,
        [field]: next.join("\n"),
        dropdownSelections: {
          ...(previous.dropdownSelections || {}),
          [field]: next,
        },
        dropdownValues: {
          ...(previous.dropdownValues || {}),
          [field]: next.join("\n"),
        },
      };
    });
  };

  const chooseTemperature = (value) =>
    setForm((previous) => ({
      ...previous,
      temperature: value,
      dropdownSelections: {
        ...(previous.dropdownSelections || {}),
        temperature: value ? [value] : [],
      },
      dropdownValues: {
        ...(previous.dropdownValues || {}),
        temperature: value,
      },
    }));

  const editChemicalQuantity = (chemical) => {
    const current = form.chemicalProducts.find(
      (item) => item.chemical === chemical
    );
    const quantity = window.prompt(
      `Enter product quantity for "${chemical}"`,
      current?.qty || ""
    );
    if (quantity === null) return;
    const products = form.chemicalProducts.map((item) =>
      item.chemical === chemical
        ? { ...item, qty: quantity.trim() }
        : item
    );
    setForm((previous) => ({
      ...previous,
      chemicalProducts: products,
      chemicalUsed: chemicalText(products),
    }));
  };

  // ---------------- Images ----------------
  const addImages = async (fileList) => {
    const files = Array.from(fileList || []).filter((file) =>
      String(file.type || "").startsWith("image/")
    );
    if (!files.length) return;
    try {
      setUploading(true);
      const folder = `images/jobCompletion/${job.id}/${occurrenceKey || "job"}`;
      const uploaded = [];
      for (const file of files) {
        const safeName = String(file.name || "photo").replace(/[^\w.-]+/g, "_");
        const path = `${folder}/${Date.now()}_${Math.random()
          .toString(36)
          .slice(2, 7)}_${safeName}`;
        const reference = storageRef(storage, path);
        await uploadBytes(reference, file, { contentType: file.type });
        const url = await getDownloadURL(reference);
        uploadedPathsRef.current.add(path);
        uploaded.push({ url, path, name: file.name || safeName });
      }
      setForm((previous) => ({
        ...previous,
        images: [...(previous.images || []), ...uploaded],
      }));
    } catch (error) {
      console.error("Image upload failed:", error);
      window.alert("One or more images could not be uploaded. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async (index) => {
    const target = form.images[index];
    setForm((previous) => ({
      ...previous,
      images: previous.images.filter((_, itemIndex) => itemIndex !== index),
    }));
    const path = typeof target === "object" ? target?.path : "";
    if (path && uploadedPathsRef.current.has(path)) {
      uploadedPathsRef.current.delete(path);
      try {
        await deleteObject(storageRef(storage, path));
      } catch (error) {
        console.warn("Could not delete removed image:", error);
      }
    }
  };

  // Cancel = nothing was submitted, so throw away photos uploaded just now.
  const cancel = async () => {
    const leftovers = Array.from(uploadedPathsRef.current);
    uploadedPathsRef.current.clear();
    await Promise.all(
      leftovers.map((path) =>
        deleteObject(storageRef(storage, path)).catch(() => null)
      )
    );
    onClose?.();
  };

  // ---------------- Submit ----------------
  const submit = async (status) => {
    if (!form.dateOfService) {
      window.alert("Please enter the service date.");
      return;
    }
    if (uploading) {
      window.alert("Please wait for the photos to finish uploading.");
      return;
    }

    try {
      setSaving(true);
      const completedAt = new Date();
      const cleanedRows = form.serviceRows.map((row) => ({
        serviceDescription: row.serviceDescription || "",
        quantity: row.quantity || "1.00",
        amount: moneyNumber(row.amount),
        total: moneyNumber(row.quantity) * moneyNumber(row.amount),
      }));
      const total = cleanedRows.reduce((sum, row) => sum + row.total, 0);

      // Still checked in and nobody overrode it: they are leaving now.
      let timeOut = form.timeOut;
      let checkOutAt = form.checkOutAt;
      let timeOutSource = form.timeOutSource;
      if (!timeEdited.out && (visitInfo.stillCheckedIn || !timeOut)) {
        timeOut = toTimeInput(completedAt);
        checkOutAt = completedAt.toISOString();
        timeOutSource = "completion";
      }

      const images = (form.images || [])
        .map((item) =>
          typeof item === "string"
            ? { url: item, path: "", name: "" }
            : { url: imageUrlOf(item), path: item.path || "", name: item.name || "" }
        )
        .filter((item) => item.url);

      const payload = {
        ...form,
        occurrenceKey,
        timeOut,
        checkOutAt,
        timeOutSource,
        images,
        jobDescription: generatedDescription,
        description: generatedDescription,
        quantity:
          cleanedRows.length === 1
            ? cleanedRows[0].quantity
            : String(cleanedRows.length),
        rate: total,
        amount: total,
        price: total,
        serviceRows: cleanedRows,
        status,
        sent: false,
        paid: false,
        completedAt: completedAt.toISOString(),
        createdAt: serverTimestamp(),
        updatedAt: new Date().toISOString(),
      };

      const invoiceReference = await addDoc(
        collection(db, "approvedInvoices"),
        payload
      );

      // Complete the job. Recurring: ONLY this occurrence, keyed by its
      // canonical date. One-time: the job itself.
      const jobUpdates = {
        approvedInvoiceId: invoiceReference.id,
        invoiceStatus: status,
        completedInvoiceData: {
          invoiceId: invoiceReference.id,
          invoicePricingSelection: payload.invoicePricingSelection,
          jobDescription: generatedDescription,
          dropdownSelections: payload.dropdownSelections,
          chemicalProducts: payload.chemicalProducts,
          chemicalUsed: payload.chemicalUsed,
          temperature: payload.temperature,
          notes: payload.notes,
          timeIn: payload.timeIn,
          timeOut: payload.timeOut,
          technicianSignature: payload.technicianSignature,
          customerSignature: payload.customerSignature,
          customerUnavailableToSign: payload.customerUnavailableToSign,
        },
        updatedAt: serverTimestamp(),
      };

      if (isRecurring(job)) {
        // A recurring job's total is the series price — one visit's invoice
        // (e.g. the Initial Fee) must not overwrite it.
        if (occurrenceKey) {
          jobUpdates.completedDates = arrayUnion(occurrenceKey);
          jobUpdates[`completedAtByDate.${occurrenceKey}`] =
            completedAt.toISOString();
          jobUpdates[`approvedInvoiceIdByDate.${occurrenceKey}`] =
            invoiceReference.id;
        }
      } else {
        jobUpdates.estimatedTotal = total;
        jobUpdates.status = "Completed";
        jobUpdates.completedAt = serverTimestamp();
        if (occurrenceKey) jobUpdates.completedDates = arrayUnion(occurrenceKey);
      }

      await updateDoc(doc(db, job.__col || "jobs", job.id), jobUpdates);

      // The job is done: close any check-in still open for this visit, with
      // the completion time as the check-out (no GPS prompt here).
      const stillOpen = (occurrenceVisitsRef.current || []).filter(
        (visit) => visit.status === "checked_in"
      );
      await Promise.all(
        stillOpen.map((visit) =>
          checkOutVisit(visit, "job_completed", null).catch((error) =>
            console.warn("Auto check-out failed:", error)
          )
        )
      );

      // Uploaded photos are now part of a saved invoice: keep them.
      uploadedPathsRef.current.clear();

      const completed = { id: invoiceReference.id, ...payload };
      setStage(status === "Draft" ? "draft-saved" : "submitted");
      try {
        await onCompleted?.(completed);
      } catch (callbackError) {
        console.error("onCompleted handler failed:", callbackError);
      }
    } catch (error) {
      console.error(error);
      window.alert(
        status === "Draft"
          ? "Could not save this invoice as a draft."
          : "Could not complete the job and add the invoice to Approved Invoices."
      );
    } finally {
      setSaving(false);
    }
  };

  const colors = ["#eff6ff", "#f0fdf4", "#fff7ed", "#faf5ff", "#fefce8"];
  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    marginTop: 5,
    marginBottom: 12,
    padding: 10,
    border: "1px solid #94a3b8",
    borderRadius: 8,
    background: "#fff",
  };
  const lockedStyle = {
    ...inputStyle,
    background: "#f1f5f9",
    color: "#334155",
    cursor: "not-allowed",
  };
  const sectionStyle = (background) => ({
    background,
    border: "1px solid #cbd5e1",
    borderRadius: 12,
    padding: 15,
    marginBottom: 16,
  });
  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
    gap: "0 14px",
  };
  const hint = { fontSize: 12, color: "#64748b", marginTop: -8, marginBottom: 10 };

  const sourceText = (source, which) => {
    if (source === "check_in") return "From the tech's job check-in";
    if (source === "check_out") return "From the tech's job check-out";
    if (source === "completion") return "Completion time (tech is still checked in)";
    if (source === "admin") return "Changed by admin";
    return which === "in"
      ? "No check-in found for this job"
      : "Set when the job is completed";
  };

  const modal = (content) =>
    createPortal(
      <div
        onMouseDown={(event) => {
          if (!saving && !uploading && event.target === event.currentTarget) {
            if (stage === "submitted" || stage === "draft-saved") onClose?.();
            else cancel();
          }
        }}
        style={{
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
          boxSizing: "border-box",
          background: "rgba(0,0,0,.62)",
          zIndex: 1999998,
        }}
      >
        <div
          onMouseDown={(event) => event.stopPropagation()}
          style={{
            width: "min(1100px, calc(100vw - 40px))",
            maxHeight: "calc(100vh - 40px)",
            overflowY: "auto",
            padding: 24,
            boxSizing: "border-box",
            background: "#fff",
            borderRadius: 14,
            boxShadow: "0 24px 80px rgba(0,0,0,.5)",
            textAlign: "left",
            zIndex: 1999999,
          }}
        >
          {content}
        </div>
      </div>,
      document.body
    );

  if (stage === "pricing") {
    return modal(
      <>
        <h2 style={{ marginTop: 0 }}>Which amount is this invoice for?</h2>
        <p>Select the invoice fee before opening the completion form.</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <button
            className="blue-button"
            onClick={() => choosePricing("initial")}
          >
            Initial Fee: ${initialFee.toFixed(2)}
          </button>
          <button
            className="green-button"
            onClick={() => choosePricing("monthly")}
          >
            Monthly Fee: ${monthlyFee.toFixed(2)}
          </button>
          <button className="red-button" onClick={cancel}>
            Cancel
          </button>
        </div>
      </>
    );
  }

  if (stage === "submitted") {
    return modal(
      <>
        <h2 style={{ marginTop: 0, color: "#166534" }}>
          Job Completed — Invoice Submitted for Review
        </h2>
        <p>
          The job was marked completed and the invoice was added to
          <strong> Approved Invoices</strong>. Nothing has been emailed to the
          client — review the invoice on the Approved Invoices page and send it
          from there.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <button className="blue-button" onClick={onClose}>
            Done
          </button>
        </div>
      </>
    );
  }

  if (stage === "draft-saved") {
    return modal(
      <>
        <h2 style={{ marginTop: 0, color: "#1d4ed8" }}>
          Invoice Saved as Draft
        </h2>
        <p>
          The job was marked completed and the invoice was saved to Approved
          Invoices as a Draft. You can finish and review it anytime from the
          Approved Invoices page.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <button className="blue-button" onClick={onClose}>
            Done
          </button>
        </div>
      </>
    );
  }

  return modal(
    <>
      <h2 style={{ marginTop: 0 }}>Complete Job &amp; Create Invoice</h2>

      <section style={sectionStyle("#eff6ff")}>
        <h3 style={{ marginTop: 0 }}>Invoice Information</h3>
        <div style={grid}>
          {LOCKED_FIELDS.map(([field, label]) => (
            <div key={field}>
              <label>
                {label} <span title="Filled in automatically" aria-hidden>🔒</span>
              </label>
              <input
                readOnly
                tabIndex={-1}
                style={lockedStyle}
                value={form[field] || ""}
                placeholder="Not on file"
              />
            </div>
          ))}
          <div>
            <label>Date of Service</label>
            <input
              type="date"
              style={inputStyle}
              value={form.dateOfService}
              onChange={(event) =>
                setForm((previous) => ({
                  ...previous,
                  dateOfService: event.target.value,
                  serviceDate: event.target.value,
                }))
              }
            />
          </div>
          <div>
            <label>Purchase Order</label>
            <input
              style={inputStyle}
              value={form.purchaseOrder}
              onChange={(event) =>
                change("purchaseOrder", event.target.value)
              }
            />
          </div>
        </div>
        <p style={{ ...hint, marginTop: 0, marginBottom: 0 }}>
          🔒 fields come from the job and the property record and can't be
          changed here. Update them on Properties &amp; Clients if they're wrong.
        </p>
      </section>

      <section style={sectionStyle("#f0fdf4")}>
        <h3 style={{ marginTop: 0 }}>Service and Amount</h3>

        {form.serviceRows.map((row, index) => (
          <div
            key={row.id}
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1fr 1fr auto",
              gap: 10,
              alignItems: "end",
              marginBottom: 12,
            }}
          >
            <div>
              {index === 0 && <label>Service Description</label>}
              <input
                style={inputStyle}
                value={row.serviceDescription}
                onChange={(event) =>
                  updateServiceRow(row.id, "serviceDescription", event.target.value)
                }
              />
            </div>
            <div>
              {index === 0 && <label>Quantity</label>}
              <input
                type="number"
                step="0.01"
                style={inputStyle}
                value={row.quantity}
                onChange={(event) =>
                  updateServiceRow(row.id, "quantity", event.target.value)
                }
              />
            </div>
            <div>
              {index === 0 && <label>Amount</label>}
              <input
                type="number"
                step="0.01"
                style={inputStyle}
                value={row.amount}
                onChange={(event) =>
                  updateServiceRow(row.id, "amount", event.target.value)
                }
              />
            </div>
            <div>
              {index === 0 && <label>Total Due</label>}
              <input
                readOnly
                style={inputStyle}
                value={(
                  moneyNumber(row.quantity) * moneyNumber(row.amount)
                ).toFixed(2)}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <button
                type="button"
                className="red-button"
                disabled={form.serviceRows.length <= 1}
                onClick={() => removeServiceRow(row.id)}
                style={{ width: "auto", padding: "9px 12px" }}
                title="Remove this service"
              >
                🗑
              </button>
            </div>
          </div>
        ))}

        <button
          type="button"
          className="blue-button"
          onClick={addServiceRow}
          style={{ width: "auto", marginBottom: 16 }}
        >
          ➕ Add Service
        </button>

        <div
          style={{
            padding: 12,
            border: "2px solid #16a34a",
            borderRadius: 10,
            background: "#dcfce7",
            fontWeight: 900,
            fontSize: 16,
            marginBottom: 16,
          }}
        >
          Invoice Total: ${invoiceTotal.toFixed(2)}
        </div>

        <div style={grid}>
          <div>
            <label>
              Time In {!isAdmin && <span aria-hidden>🔒</span>}
            </label>
            <input
              type="time"
              readOnly={!isAdmin}
              style={isAdmin ? inputStyle : lockedStyle}
              value={form.timeIn}
              onChange={(event) => changeTime("in", event.target.value)}
            />
            <div style={hint}>
              {visitInfo.loading
                ? "Looking up the tech's check-in…"
                : `${sourceText(form.timeInSource, "in")}${
                    form.checkInAt && form.timeInSource !== "admin"
                      ? ` (${prettyTime(toDateSafe(form.checkInAt))})`
                      : ""
                  }`}
            </div>
          </div>
          <div>
            <label>
              Time Out {!isAdmin && <span aria-hidden>🔒</span>}
            </label>
            <input
              type="time"
              readOnly={!isAdmin}
              style={isAdmin ? inputStyle : lockedStyle}
              value={form.timeOut}
              onChange={(event) => changeTime("out", event.target.value)}
            />
            <div style={hint}>
              {visitInfo.loading
                ? "Looking up the tech's check-out…"
                : sourceText(form.timeOutSource, "out")}
            </div>
          </div>
        </div>
        {isAdmin && (
          <p style={{ ...hint, marginTop: 0 }}>
            Admins can correct Time In / Time Out if the check-in was missed or
            wrong.
          </p>
        )}
      </section>

      <section style={sectionStyle("#fff7ed")}>
        <h3 style={{ marginTop: 0 }}>Treatment Details</h3>
        {dynamicDropdowns.map((dropdown, index) => (
          <React.Fragment key={dropdown.id || dropdown.fieldName}>
            <MultiSelectField
              dropdown={dropdown}
              selectedValues={
                form.dropdownSelections?.[dropdown.fieldName] || []
              }
              disabled={saving}
              background={colors[index % colors.length]}
              onToggle={(option) => toggleDropdown(dropdown, option)}
            />
            {dropdown.fieldName === "chemicalUsed" &&
              form.chemicalProducts.length > 0 && (
                <div style={{ margin: "-5px 0 14px 12px" }}>
                  {form.chemicalProducts.map((item) => (
                    <div
                      key={item.chemical}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 6,
                      }}
                    >
                      <strong>{item.chemical}</strong>
                      <span>Quantity: {item.qty || "—"}</span>
                      <button
                        type="button"
                        className="blue-button"
                        style={{ width: "auto", padding: "5px 9px" }}
                        onClick={() =>
                          editChemicalQuantity(item.chemical)
                        }
                      >
                        Edit Quantity
                      </button>
                    </div>
                  ))}
                </div>
              )}
          </React.Fragment>
        ))}

        <div
          style={{
            marginBottom: 14,
            padding: 12,
            background: "#eff6ff",
            border: "1px solid #cbd5e1",
            borderRadius: 10,
          }}
        >
          <label style={{ display: "block", fontWeight: 800, marginBottom: 7 }}>
            {temperatureLabel} (°F)
          </label>
          <select
            style={{ ...inputStyle, marginTop: 0, marginBottom: 0, maxWidth: 260 }}
            value={form.temperature}
            disabled={saving}
            onChange={(event) => chooseTemperature(event.target.value)}
          >
            <option value="">Select temperature…</option>
            {TEMPERATURE_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section style={sectionStyle("#faf5ff")}>
        <h3 style={{ marginTop: 0 }}>Generated Job Description</h3>
        <p style={{ marginTop: 0, color: "#475569" }}>
          This contains only the dropdown selections made on this form.
        </p>
        <textarea
          readOnly
          style={{ ...inputStyle, minHeight: 190, background: "#fff" }}
          value={generatedDescription}
        />
      </section>

      <section style={sectionStyle("#f8fafc")}>
        <h3 style={{ marginTop: 0 }}>Notes &amp; Photos</h3>
        <label>Notes</label>
        <textarea
          style={{ ...inputStyle, minHeight: 110 }}
          value={form.notes}
          disabled={saving}
          placeholder="Anything the office or the client should know about this visit…"
          onChange={(event) => change("notes", event.target.value)}
        />

        <label style={{ display: "block", marginBottom: 6 }}>Photos</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
          {(form.images || []).map((item, index) => {
            const url = imageUrlOf(item);
            if (!url) return null;
            return (
              <div
                key={`${url}_${index}`}
                style={{
                  position: "relative",
                  width: 110,
                  height: 110,
                  borderRadius: 10,
                  overflow: "hidden",
                  border: "1px solid #cbd5e1",
                  background: "#fff",
                }}
              >
                <a href={url} target="_blank" rel="noreferrer">
                  <img
                    src={url}
                    alt={(typeof item === "object" && item.name) || `Photo ${index + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                </a>
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  disabled={saving}
                  title="Remove photo"
                  style={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    width: 26,
                    height: 26,
                    padding: 0,
                    borderRadius: "50%",
                    border: "none",
                    background: "rgba(220,38,38,.92)",
                    color: "#fff",
                    fontWeight: 900,
                    cursor: "pointer",
                    lineHeight: "26px",
                  }}
                >
                  ×
                </button>
              </div>
            );
          })}
          {!(form.images || []).length && (
            <span style={{ color: "#64748b" }}>No photos added yet.</span>
          )}
        </div>
        <label
          className="blue-button"
          style={{
            display: "inline-block",
            width: "auto",
            cursor: saving || uploading ? "default" : "pointer",
            opacity: saving || uploading ? 0.6 : 1,
          }}
        >
          {uploading ? "Uploading…" : "➕ Add Photos"}
          <input
            type="file"
            accept="image/*"
            multiple
            disabled={saving || uploading}
            style={{ display: "none" }}
            onChange={(event) => {
              addImages(event.target.files);
              event.target.value = "";
            }}
          />
        </label>
      </section>

      <section style={sectionStyle("#fefce8")}>
        <h3 style={{ marginTop: 0 }}>Signatures and Submission</h3>
        <SignaturePadField
          label="Technician Signature"
          value={form.technicianSignature}
          disabled={saving}
          onChange={(value) => change("technicianSignature", value)}
        />
        <SignaturePadField
          label="Customer Signature"
          value={form.customerSignature}
          disabled={saving || form.customerUnavailableToSign}
          unavailableText={
            form.customerUnavailableToSign ? "Signature Not Necessary" : ""
          }
          onChange={(value) => change("customerSignature", value)}
        />
        <label
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 4,
            margin: "0 0 15px 0",
            textAlign: "left",
          }}
        >
          <input
            type="checkbox"
            checked={form.customerUnavailableToSign}
            disabled={saving}
            onChange={(event) =>
              change("customerUnavailableToSign", event.target.checked)
            }
            style={{ width: 16, height: 16, margin: 0, flex: "0 0 16px" }}
          />
          <span>Customer is unavailable to sign</span>
        </label>
        <div style={grid}>
          <div>
            <label>Submitted Date</label>
            <input
              type="date"
              style={inputStyle}
              value={form.submittedDate}
              onChange={(event) =>
                change("submittedDate", event.target.value)
              }
            />
          </div>
          <div>
            <label>Submitted By</label>
            <select
              style={inputStyle}
              value={form.submittedBy}
              onChange={(event) => change("submittedBy", event.target.value)}
            >
              <option value="">Select…</option>
              {usersList.map((user) => {
                const name = formatUserName(user);
                return name ? (
                  <option key={user.id} value={name}>
                    {name}
                  </option>
                ) : null;
              })}
            </select>
          </div>
        </div>
      </section>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        <button
          className="green-button"
          onClick={() => submit("Approved")}
          disabled={saving || uploading}
        >
          {saving ? "Submitting..." : "Submit — Complete Job & Send to Approved Invoices"}
        </button>
        <button
          className="grey-button"
          onClick={() => submit("Draft")}
          disabled={saving || uploading}
        >
          {saving ? "Saving..." : "Save as Draft"}
        </button>
        <button className="red-button" onClick={cancel} disabled={saving || uploading}>
          Cancel
        </button>
      </div>
    </>
  );
}
